import {
  CircleExclamationRegularIcon,
  CircleInfoRegularIcon,
} from "@elixir/icons";
import React from "react";
import OTPInput from "react-otp-input";
import { LinkButton } from "ui";
import usePostHprResendAadhaarOtp from "../../../apis/axon/hpr/use-post-hpr-resend-aadhaar-otp";
import usePostHprVerifyAadhaarOtp from "../../../apis/axon/hpr/use-post-hpr-verify-aadhaar-otp";
import useHprHfrOnboardingStore from "../../../stores/hpr-hfr-dhis-onboarding";
import { OnboardingNavigationStates } from "../../../stores/hpr-hfr-dhis-onboarding/enums";
import VerifyFlowContainer from "../verify-flow-container";
import { track } from "../../../utils/tracking";
import { HFR_HPR_MIXPANEL_EVENTS } from "@elixir/types";
import usePostHprAadhaarOtp from "../../../apis/axon/hpr/use-post-hpr-aadhaar-otp";

const NO_OF_FIELDS = 6;

const OTP_RESEND_TIME = 50;

const OtpErrorComponent = ({ errorMessage }: { errorMessage: string }) => {
  return (
    <div className="dr-flex dr-space-x-8 dr-items-center dr-text-darwin-red-primary">
      <CircleExclamationRegularIcon className="dr-w-12 dr-h-12" />
      <div className="CalloutRegular">{errorMessage}</div>
    </div>
  );
};

const ResendCodeComponent = ({
  handleResendCode,
}: {
  handleResendCode: () => void;
}) => {
  return <LinkButton title="Resend code" onClick={handleResendCode} />;
};

const ResendTimerComponent = ({
  secondsRemaining,
}: {
  secondsRemaining: string;
}) => {
  return (
    <div className="BodyRegular dr-text-darwin-neutral-600">
      {`Resend in 00:${secondsRemaining}`}
    </div>
  );
};

const VerifyAadhaarOtp = () => {
  const [otp, setOtp] = React.useState("");
  const [timerSeconds, setTimerSeconds] =
    React.useState<number>(OTP_RESEND_TIME);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const resendTimerIntervalId = React.useRef<NodeJS.Timeout>();
  const setTransactionId = useHprHfrOnboardingStore(
    (state) => state.setTransactionId
  );  
  const setMaskedMobileNumber = useHprHfrOnboardingStore(
    (state) => state.setMaskedMobileNumber
  );
  const aadhaarNumber = useHprHfrOnboardingStore(
    (state) => state.aadhaarNumber
  );

  const { mutateAsync: postOtp, isLoading: isPostHprAadhaarLoading } = usePostHprAadhaarOtp();
  


  React.useEffect(() => {
    resendTimerIntervalId.current = setInterval(() => {
      setTimerSeconds((timer) => timer - 1);
    }, 1000);

    return () => {
      clearInterval(resendTimerIntervalId.current);
    };
  }, []);

  React.useEffect(() => {
    if (timerSeconds < 20) {
      setErrorMessage("Servers are running slow. OTP might be delayed!");
    }
    if (timerSeconds < 1) {
      setErrorMessage("");
      clearInterval(resendTimerIntervalId.current);
      setTimerSeconds(-1);
    }
  }, [timerSeconds]);

  React.useEffect(() => {
    track(HFR_HPR_MIXPANEL_EVENTS.VERIFY_AADHAAR_OTP_PAGE_VIEW);
  }, []);

  const transactionId = useHprHfrOnboardingStore(
    (state) => state.transactionId
  );
  const setActiveScreen = useHprHfrOnboardingStore(
    (state) => state.setActiveScreen
  );

  const maskedMobileNumber = useHprHfrOnboardingStore(
    (state) => state.maskedMobileNumber
  );

  const { mutateAsync: postVerifyOtp, isLoading: isVerifyOtpLoading } =
    usePostHprVerifyAadhaarOtp();
  const { mutateAsync: postResendOtp, isLoading: isResendLoading } =
    usePostHprResendAadhaarOtp();

  const handleOtpChange = async (newOtp: string | undefined = otp) => {
    setOtp(newOtp);
    if (newOtp.length === NO_OF_FIELDS && transactionId) {
      const { success, error } = await postVerifyOtp({
        otp: newOtp,
        txn_id: transactionId,
      });
      track(HFR_HPR_MIXPANEL_EVENTS.SUBMIT_AADHAR_OTP, {
        success: `${success}`,
        error: `${error?.actualMessage || error?.message}`,
      });
      if (success) {
        setActiveScreen(OnboardingNavigationStates.PHONE_NUMBER);
      } else {
        setErrorMessage("Please try again");
        setTimerSeconds(-1);
      }
    }
  };

  const handleResend = async () => {
    if (!aadhaarNumber) {
      return;
    }
    
    const { success, data, error } = await postOtp({ aadharNumber: aadhaarNumber });

    track(HFR_HPR_MIXPANEL_EVENTS.RESEND_AADHAR_OTP, {
      success: `${success}`,
      error: `${error?.actualMessage || error?.message}`,
    });

    if (success && data) {
      const { txn_id, mobile_num } = data;
      setTransactionId(txn_id);
      setMaskedMobileNumber(mobile_num);

      setTimerSeconds(OTP_RESEND_TIME);
    }
  };

  return (
    <VerifyFlowContainer
      title="Verify yourself via Aadhaar"
      secondaryButtonClick={
        isVerifyOtpLoading
          ? undefined
          : () => {
              setActiveScreen(OnboardingNavigationStates.AADHAR);
            }
      }
      secondaryButtonText={"Back"}
      primaryButtonText="Verify OTP"
      primaryButtonClick={() => handleOtpChange()}
      primaryButtonLoading={isVerifyOtpLoading || isResendLoading}
    >
      <div className="dr-py-12 dr-space-y-16">
        <OTPInput
          inputStyle="dr-rounded-8 dr-border-1 dr-p-10 dr-border-darwin-primary-primary BodyRegular dr-w-36 dr-bg-darwin-neutral-0 dr-text-darwin-neutral-1000 placeholder:dr-text-darwin-neutral-600 dr-outline-none dr-text-center"
          value={otp}
          onChange={handleOtpChange}
          numInputs={NO_OF_FIELDS}
          placeholder="______"
          renderInput={(props) => (
            <input
              {...props}
              className="BodyRegular dr-w-36 dr-border-1 dr-border-darwin-touch-neutral-200 dr-rounded-8 dr-px-6 dr-py-10 dr-text-center"
            />
          )}
          skipDefaultStyles={true}
          renderSeparator={() => <div className="dr-p-4" />}
          shouldAutoFocus={true}
        />
        <div className="dr-space-y-4">
          {errorMessage || timerSeconds === -1 ? (
            <div className="dr-flex dr-items-center dr-justify-start dr-space-x-8">
              {errorMessage ? (
                <OtpErrorComponent errorMessage={errorMessage} />
              ) : null}
              {timerSeconds === -1 ? (
                <ResendCodeComponent handleResendCode={handleResend} />
              ) : null}
            </div>
          ) : null}
          {timerSeconds > -1 ? (
            <ResendTimerComponent
              secondsRemaining={timerSeconds.toString().padStart(2, "0")}
            />
          ) : null}
          {maskedMobileNumber ? (
            <div className="dr-flex dr-items-start dr-space-x-8 dr-text-darwin-neutral-600 dr-pt-4">
              <CircleInfoRegularIcon className="dr-min-w-12 dr-w-12 dr-min-h-12 dr-h-12 dr-mt-2" />
              <p className="FootnoteRegular">
                {`OTP will be recieved on the phone number linked with your Aadhaar - ${maskedMobileNumber}`}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </VerifyFlowContainer>
  );
};

export default VerifyAadhaarOtp;
